// Whether a grounded control has anything to show for a given value shape.
//
// Its own module because two components need the same answer: the panel, to decide what to render, and
// the clause above it, to decide whether the toggle that OPENS the panel should exist at all. Gating the
// toggle on the control merely existing left a live chevron that revealed nothing — a dead affordance,
// which the repo's UI rules forbid.

import { isFiniteNumber } from '@/_shared/lib/numeric';

import type { ConditionValue, DistributionEncoding, RowControl, ValueShape } from '../../types';
import { insightHasData } from '../types';
import { domainForShape, isWindow } from './distribution-domains';

type Kind = RowControl['kind'];

/** The numeric-domain controls: the measured histogram, quantile bands, and the approximated slider. */
const NUMERIC_KINDS = new Set<Kind>(['distribution', 'segments', 'quantiles']);
const SEGMENT_KINDS = new Set<Kind>(['segments']);
const PILL_KINDS = new Set<Kind>(['pills']);

/** Which control kinds suit a shape, or `undefined` when the shape takes no grounded control at all. */
export function allowedKinds(shape: ValueShape): Set<Kind> | undefined {
  switch (shape) {
    case 'single_enum':
    case 'multi_enum':
      return PILL_KINDS;
    case 'number':
    case 'number_range':
    case 'date':
    case 'date_range':
    case 'relative_window':
      return NUMERIC_KINDS;
    case 'none':
      return SEGMENT_KINDS;
    // A boolean column is a 2-value enum, and its `true`/`false` counts are available once `topK` has
    // been run for it (it is an opt-in, not part of `DEFAULT_METRICS.boolean`). ACT2-558 opened the
    // round trip that lets the panel spend them.
    case 'boolean':
      return PILL_KINDS;
    case 'text':
      return undefined;
    default: {
      const _exhaustive: never = shape;
      return _exhaustive;
    }
  }
}

export function insightRenders(control: RowControl | undefined, shape: ValueShape): boolean {
  const kinds = allowedKinds(shape);
  return !!kinds && !!control && insightHasData(control) && kinds.has(control.kind);
}

/**
 * The encodings a measurement can actually arrive in: a numeric column is measured as `number`, and a
 * temporal one as `days_ago` under the recency operators or `day_index` otherwise. `days_ahead` is a
 * READING domain with no measuring counterpart — nothing ever produces forward-counted bins — so a
 * mismatch against it is permanent, not stale, and must not be reported as one.
 */
const MEASURABLE_ENCODINGS: ReadonlySet<DistributionEncoding> = new Set(['number', 'days_ago', 'day_index']);

/**
 * What the panel does with this control under this operator: render it, say it is withheld, or nothing.
 *
 * `'stale'` is a distribution whose bins are in a different encoding than the operator reads — a chart of
 * the wrong numbers, which a fresh read in the new operator's encoding replaces. The encoding comes off
 * the domain that will actually READ the value, not a parallel switch: a switch returns a concrete answer
 * for an operator nobody listed, so a new one would slip past the guard rather than be caught by it.
 *
 * A mismatch no read can resolve is `'none'`, not `'stale'`: the withheld line promises the chart returns
 * once the rule is re-counted, and under a forward window it never does.
 *
 * `'grounding'` is the panel with no widget in it: nothing fits, but the COLUMN was measured, so there is
 * still something true to say about it. That state is what lets the toggle exist on a rule the probe
 * could not draw a control for — "no button that opens nothing" is about opening NOTHING, and a fill rate
 * is not nothing. It is decided by the same {@link fillRateLine} the panel renders, so the toggle and the
 * panel cannot disagree about whether there is anything in there.
 */
export type InsightState = 'control' | 'stale' | 'grounding' | 'none';

export function insightState(
  control: RowControl | undefined,
  shape: ValueShape,
  operator: string,
  value: ConditionValue,
  nonNullFraction?: number,
): InsightState {
  // What is left when no control renders. Decided on the MEASUREMENT, not on whether a sentence builder
  // happened to return a string: coupling a state-machine member to the wording of copy means an editor
  // returning `''` yields an empty italic line behind a live toggle, with no type error to catch it.
  const withoutControl: InsightState = isFiniteNumber(nonNullFraction) ? 'grounding' : 'none';
  if (!control || !insightRenders(control, shape)) return withoutControl;
  if (control.kind !== 'distribution' || !control.encoding) return 'control';
  const domain = domainForShape(shape, undefined, operator, isWindow(value) ? value.unit : undefined);
  if (control.encoding === domain.encoding) return 'control';
  return MEASURABLE_ENCODINGS.has(domain.encoding) ? 'stale' : withoutControl;
}
